import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  IconButton,
  Grid,
  InputLabel,
  TextField,
  Switch,
  Typography,
  FormControlLabel,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { makeStyles } from "@mui/styles";
import CustomButton from "../../Custom/CustomButton";
import DatePicker from "react-datepicker";
import instance from "../../../globals/axios";
import { getFarmerPlotId } from "../../../globals/utils";
import { useAlert } from "../../Alert";
import EditIcon from "../../../assets/svg/EditIcon.svg";
import RejectionPopup from "./RejectionPopup";
import SuccessPopup from "./SuccessPopup";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    fontSize: 16,
  },
  dialogContent: {
    height: 400,
  },
  datepicker: {
    display: "none",
  },
  taxtLabel: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "400 !important",
    color: "#000",
    marginTop: "10px",
  },
  taxtTitle: {
    fontSize: "12px !important",
    fontFamily: "Inter",
    fontWeight: "700 !important",
    color: "#000",
  },
}));

const HarvestingPopup = ({ onClose, t }) => {
  const classes = useStyles();
  const [open, setOpen] = useState(true);
  const [dateOfHarvesting, setDateOfHarvesting] = useState(null);
  const [requestDate, setRequestDate] = useState(null);
  const [actualDate, setActualDate] = useState(null);
  const [formData, setFormData] = useState({});
  const [existingData, setExistingData] = useState(null);
  const [comment, setComment] = useState("");
  const [isEditMode, setIsEditMode] = useState(false);
  const [openRejectionPopup, setOpenRejectionPopup] = useState(false);
  const [dateErrors, setDateErrors] = useState({
    dateOfHarvesting: "",
    requestDate: "",
    actualDate: "",
    dateOfGrading: "",
    dateOfDispatch: "",
  });
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const roles = "FARMER";
  let farmerPlotId = getFarmerPlotId();
  const { showAlert } = useAlert();

  const rows = [
    {
      label: t("Date of Harvesting"),
      type: "date",
      field: "dateOfHarvesting",
      key: "harvesting_date",
    },
    {
      label: t("Request Date of Lifting"),
      type: "date",
      field: "requestDate",
      key: "requested_date_of_lifting",
    },
    {
      label: t("Proper Skin set"),
      type: "toggle",
      field: "properSkinSet",
      key: "proper_skin_set",
    },
    {
      label: t("Proper grading & sorting done as per Hyfun criteria"),
      type: "toggle",
      field: "gradingSorting",
      key: "proper_grading_sorting",
    },
    {
      label: t("Bags/Bulk"),
      type: "toggle",
      field: "bagsBulk",
      key: "toggle",
    },

    {
      label: t("Actual Date of Lifting"),
      type: "date",
      field: "actualDate",
      key: "actual_date_of_lifting",
    },
  ];

  useEffect(() => {
    if (farmerPlotId) {
      const fetchData = async () => {
        try {
          const response = await instance(
            `plotengagement/get-harvesting?farmer_plot=${farmerPlotId}`
          );
          let data = response.data;
          if (data.length > 0) {
            setExistingData(data[0]);
          }
        } catch (error) {
          console.error("Error fetching data:", error);
        }
      };
      fetchData();
    }
  }, [farmerPlotId]);

  useEffect(() => {
    if (existingData) {
      setDateOfHarvesting(
        existingData.harvesting_date
          ? new Date(existingData.harvesting_date)
          : null
      );
      setComment(existingData.comments ? existingData.comments : "");
      setRequestDate(
        existingData.requested_date_of_lifting
          ? new Date(existingData.requested_date_of_lifting)
          : null
      );
      setActualDate(
        existingData.actual_date_of_lifting
          ? new Date(existingData.actual_date_of_lifting)
          : null
      );
      setFormData({
        proper_skin_set: existingData.proper_skin_set ? "yes" : "no",
        proper_grading_sorting: existingData.proper_grading_sorting
          ? "yes"
          : "no",
        toggle: existingData.type === "BULK" ? "yes" : "no",
      });
    }
  }, [existingData]);

  const handleDateChange = (date, field) => {
    if (field === "harvesting_date") {
      setDateOfHarvesting(date);
      setDateErrors({ ...dateErrors, dateOfHarvesting: "" });
    } else {
      if (field === "requested_date_of_lifting") {
        setRequestDate(date);
        setDateErrors({ ...dateErrors, requestDate: "" });
      } else if (field === "actual_date_of_lifting") {
        setActualDate(date);
        setDateErrors({ ...dateErrors, actualDate: "" });
      }
    }
  };

  const handleCommentChange = (event) => {
    setComment(event.target.value);
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleToggleChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleInputChange = (field, value) => {
    setFormData((prevData) => ({
      ...prevData,
      [field]: value,
    }));
  };

  const handleClose = () => {
    setOpen(false);
    onClose();
  };

  const handleReject = (reason) => {
    const rejectionData = {
      id: existingData.id,
      stage: "harvesting",
      status: "REJECTED",
      reason_for_rejection: reason,
    };

    instance
      .post("plotengagement/ao-status-update", rejectionData)
      .then(() => {
        setOpenRejectionPopup(false);
        showAlert("success", "Data Rejected");
        setOpen(false);
        onClose();
      })
      .catch((error) => {
        console.error("Error rejecting data:", error);
      });
  };

  const handleApprove = () => {
    const approvalData = {
      id: existingData.id,
      stage: "harvesting",
      status: "APPROVED",
    };

    instance
      .post("plotengagement/ao-status-update", approvalData)
      .then(() => {
        showAlert("success", "Data Approved");
        setOpen(false);
        onClose();
      })
      .catch((error) => {
        console.error("Error approving data:", error);
      });
  };

  const validateDates = () => {
    let isValid = true;
    const errors = { ...dateErrors };

    if (!dateOfHarvesting) {
      errors.dateOfHarvesting = "Please select the date of harvesting.";
      isValid = false;
    }
    if (!requestDate) {
      errors.requestDate = "Please select the request date of lifting.";
      isValid = false;
    }
    if (!actualDate) {
      errors.actualDate = "Please select the actual date of lifting.";
      isValid = false;
    }

    setDateErrors(errors);
    return isValid;
  };

  const handleSubmit = () => {
    const isDatesValid = validateDates();
    if (isDatesValid) {
      const formatDate = (date) => {
        const pad = (num) => (num < 10 ? `0${num}` : `${num}`);
        return date
          ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(
              date.getDate()
            )}`
          : null;
      };

      const postData = {
        harvesting_date: dateOfHarvesting ? formatDate(dateOfHarvesting) : null,
        comments: comment,
        is_seed: false,
      };
      postData.proper_skin_set = formData["proper_skin_set"] === "yes";
      postData.proper_grading_sorting =
        formData["proper_grading_sorting"] === "yes";
      postData.requested_date_of_lifting = requestDate
        ? formatDate(requestDate)
        : null;
      postData.actual_date_of_lifting = actualDate
        ? formatDate(actualDate)
        : null;
      postData.type = formData["toggle"] === "yes" ? "BULK" : "BAG";

      if (isEditMode) {
        postData.id = existingData.id;
      } else {
        postData.farmer_plot = farmerPlotId;
      }
      if (existingData && existingData.status === "REJECTED") {
        postData.resubmit = true;
      }

      const apiUrl = isEditMode
        ? "plotengagement/update-harvesting"
        : "plotengagement/create-harvesting";

      instance
        .post(apiUrl, postData)
        .then((res) => {
          if (res && res.data) {
            setShowSuccessPopup(true);
            setTimeout(() => {
              setShowSuccessPopup(false);
              onClose();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error("Error creating/updating harvesting", err);
          showAlert("error", "Some Error Occurred");
        });
    }
  };

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="xs"
        style={{ zIndex: 1 }}
        PaperProps={{ style: { maxHeight: "calc(100% - 25%)" } }}
      >
        <div style={{ padding: "16px" }}>
          <Grid container>
            <Grid item xs={10}>
              <DialogTitle style={{ padding: "0px" }}>
                {t("Harvesting")}
              </DialogTitle>
            </Grid>
            {existingData !== null ? (
              <Grid item xs={1} style={{ marginTop: "10px" }}>
                <img src={EditIcon} onClick={handleEditClick} alt="Edit" />
              </Grid>
            ) : null}
            <Grid item xs={1}>
              <IconButton
                aria-label="close"
                className={classes.closeButton}
                onClick={handleClose}
              >
                <CloseIcon />
              </IconButton>
            </Grid>
          </Grid>

          <div>
            {rows.map((row, index) => (
              <Grid key={index} container style={{ marginTop: "10px" }}>
                <Grid item xs={row.type === "toggle" ? 9 : 7}>
                  <InputLabel style={{ fontSize: "12px", marginTop: "10px" }}>
                    {row.key === "proper_grading_sorting" ? (
                      <>
                        {t("Proper grading & sorting done as per")} <br />{" "}
                        {t("Hyfun criteria")}
                      </>
                    ) : (
                      t(row.label)
                    )}
                  </InputLabel>
                </Grid>

                <Grid item xs={3}>
                  {row.type === "toggle" && (
                    <div style={{ display: "flex" }}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={formData[row.key] === "yes"}
                            onChange={(event) =>
                              handleToggleChange(
                                row.key,
                                event.target.checked ? "yes" : "no"
                              )
                            }
                            disabled={!isEditMode && existingData !== null}
                          />
                        }
                        label={
                          row.key === "toggle"
                            ? t("Bulk")
                            : formData[row.key] === "yes"
                            ? t("Yes")
                            : t("No")
                        }
                        labelPlacement="end"
                      />
                    </div>
                  )}
                  {row.type === "date" && (
                    <>
                      <TextField
                        placeholder="DD/MM/YYYY"
                        style={{ marginLeft: "10px" }}
                        error={Boolean(dateErrors[row.field])}
                        helperText={dateErrors[row.field]}
                        disabled={!isEditMode && existingData !== null}
                        value={
                          row.key === "harvesting_date"
                            ? dateOfHarvesting
                              ? dateOfHarvesting.toLocaleDateString()
                              : ""
                            : row.key === "requested_date_of_lifting"
                            ? requestDate
                              ? requestDate.toLocaleDateString()
                              : ""
                            : row.key === "actual_date_of_lifting"
                            ? actualDate
                              ? actualDate.toLocaleDateString()
                              : ""
                            : formData[row.key]
                            ? formData[row.key].toLocaleDateString()
                            : ""
                        }
                        InputProps={{
                          readOnly: true,
                          sx: {
                            height: "40px",
                            borderRadius: "5px",
                            marginTop: "1px",
                            width: "120px",
                            background: "#FFF",
                            border: "1px solid #C7BDBD",
                          },
                        }}
                        onClick={() =>
                          document
                            .getElementById(
                              row.key.toLowerCase().split(" ").join("-") +
                                "-picker"
                            )
                            .click()
                        }
                      />
                      <DatePicker
                        id={
                          row.key.toLowerCase().split(" ").join("-") + "-picker"
                        }
                        dateFormat="dd/MM/yyyy"
                        selected={
                          row.key === "harvesting_date"
                            ? dateOfHarvesting
                            : row.key === "requested_date_of_lifting"
                            ? requestDate
                            : row.key === "actual_date_of_lifting"
                            ? actualDate
                            : formData[row.key]
                        }
                        onChange={(date) => handleDateChange(date, row.key)}
                        className={classes.datepicker}
                      />
                    </>
                  )}
                  {row.type === "text" && (
                    <TextField
                      value={formData[row.key] || ""}
                      onChange={(e) =>
                        handleInputChange(row.key, e.target.value)
                      }
                      disabled={!isEditMode && existingData !== null}
                      InputProps={{
                        sx: {
                          height: "40px",
                          borderRadius: "5px",
                          marginTop: "1px",
                          width: "120px",
                          background: "#FFF",
                          border: "1px solid #C7BDBD",
                          marginLeft: "10px",
                        },
                      }}
                    />
                  )}
                </Grid>
              </Grid>
            ))}
          </div>
        </div>

        <div style={{ display: "grid", padding: "10px" }}>
          <InputLabel>{t("Comment(Optional)")}</InputLabel>
          <TextField
            multiline
            rows={1}
            value={comment}
            onChange={handleCommentChange}
            disabled={!isEditMode && existingData !== null}
          />
          {existingData &&
            existingData.status === "REJECTED" &&
            existingData.reason_for_rejection &&
            !isEditMode &&
            roles === "FARMER" && (
              <Typography style={{ marginTop: "10px", color: "red" }}>
                {t("Reason for Rejection")}: {existingData.reason_for_rejection}
              </Typography>
            )}
        </div>

        <div
          style={{ display: "flex", margin: "auto", justifyContent: "center" }}
        >
          {existingData && isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Update")}
              </CustomButton>
            </div>
          ) : !existingData && !isEditMode ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
                width: "140px",
              }}
            >
              <CustomButton onClick={handleSubmit} hideIcon>
                {t("Submit")}
              </CustomButton>
            </div>
          ) : null}
          {existingData &&
          existingData.status === "PENDING" &&
          !isEditMode &&
          (roles === "AO" || roles === "SPVR") ? (
            <div
              style={{
                display: "flex",
                margin: "auto",
                justifyContent: "center",
              }}
            >
              <CustomButton
                omClick={() => setOpenRejectionPopup(true)}
                hideIcon
              >
                {t("Reject")}
              </CustomButton>
              <div style={{ marginLeft: "10px" }}></div>
              <CustomButton onClick={handleApprove} hideIcon>
                {t("Approve")}
              </CustomButton>
            </div>
          ) : null}
        </div>
        <RejectionPopup
          open={openRejectionPopup}
          handleClose={() => setOpenRejectionPopup(false)}
          handleSubmit={handleReject}
        />
        {showSuccessPopup && (
          <SuccessPopup onClose={() => setShowSuccessPopup(false)} />
        )}
      </Dialog>
    </div>
  );
};

export default HarvestingPopup;
